import { useState } from 'react';
import type { TicketDetail, TicketType } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';

interface Props {
  ticket: TicketDetail;
  onSaved: () => void | Promise<void>;
  /**
   * Keys the server owns and will refuse to change here — today, the
   * quote fields of a ticket that has a quote document. Shown read-only
   * with a note rather than hidden, because the values still matter on
   * this ticket; it is only the place you edit them that has moved.
   */
  lockedKeys?: readonly string[];
  /** Where the locked values are edited instead. */
  lockedReason?: string;
  /**
   * Keys this reader may not see at all — today, `quoted_amount_usd` for
   * anyone who is not a quote agent. Distinct from `lockedKeys`: a locked
   * field is shown and not editable, a hidden one is not shown. The
   * server withholds the value too; this stops the label advertising a
   * blank where money used to be.
   */
  hiddenKeys?: readonly string[];
}

interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'number' | 'url';
  options?: { value: string; label: string }[];
  parse?: (raw: string) => unknown;
}

const PASSWORD_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'partial', label: 'Partial' },
];

const URGENCY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const REQUEST_KIND_OPTIONS = [
  { value: 'nda_copy', label: 'Copy of NDA' },
  { value: 'contract_copy', label: 'Copy of contract' },
  { value: 'poc_change', label: 'Change of point of contact' },
  { value: 'hours_change', label: 'Change of monthly block hours' },
];

function fieldsForType(type: TicketType): FieldDef[] {
  switch (type) {
    case 'escalation':
      return [
        { key: 'client_name', label: 'End client', type: 'text' },
        { key: 'urgency', label: 'Urgency', type: 'select', options: URGENCY_OPTIONS },
        { key: 'steps_taken', label: 'Steps taken', type: 'textarea' },
        { key: 'test_machine', label: 'Test machine', type: 'text' },
        { key: 'password_access', label: 'Password access', type: 'select', options: PASSWORD_OPTIONS },
      ];
    case 'project':
      return [
        { key: 'client_name', label: 'End client', type: 'text' },
        { key: 'urgency', label: 'Urgency', type: 'select', options: URGENCY_OPTIONS },
        { key: 'time_frame', label: 'Time frame', type: 'text' },
        { key: 'password_access', label: 'Password access', type: 'select', options: PASSWORD_OPTIONS },
      ];
    case 'quote':
      return [
        { key: 'sow_summary', label: 'SOW summary', type: 'textarea' },
        { key: 'quoted_hours', label: 'Quoted hours', type: 'number', parse: (v) => Number(v) },
        { key: 'quoted_amount_usd', label: 'Quoted amount (USD)', type: 'number', parse: (v) => (v === '' ? null : Number(v)) },
        { key: 'qwilr_quote_url', label: 'Qwilr quote URL', type: 'url' },
      ];
    case 'administrative':
      return [
        { key: 'client_name', label: 'End client', type: 'text' },
        { key: 'urgency', label: 'Urgency', type: 'select', options: URGENCY_OPTIONS },
        { key: 'request_kind', label: 'Request kind', type: 'select', options: REQUEST_KIND_OPTIONS },
        { key: 'new_poc_name', label: 'New POC name', type: 'text' },
        { key: 'new_poc_email', label: 'New POC email', type: 'text' },
        { key: 'new_monthly_hours', label: 'New monthly hours', type: 'number', parse: (v) => (v === '' ? null : Number(v)) },
        { key: 'notes', label: 'Notes', type: 'textarea' },
      ];
    case 'chat':
    default:
      return [];
  }
}

export function CustomFieldsPanel({
  ticket,
  onSaved,
  lockedKeys,
  lockedReason,
  hiddenKeys,
}: Props): React.ReactElement | null {
  const fields = fieldsForType(ticket.type as TicketType).filter(
    (f) => !hiddenKeys?.includes(f.key),
  );
  const isLocked = (key: string): boolean => lockedKeys?.includes(key) ?? false;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>(() => initialDraft(ticket, fields));

  if (fields.length === 0) return null;

  function startEdit() {
    setDraft(initialDraft(ticket, fields));
    setError(null);
    setEditing(true);
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const f of fields) {
        // Server-owned keys are never posted. Sending them back unchanged
        // would pass the server's guard, but a number round-tripped
        // through a text input is exactly how an unintended edit sneaks
        // in; leaving them out makes that impossible.
        if (isLocked(f.key)) continue;
        const raw = draft[f.key] ?? '';
        if (f.parse) {
          const parsed = f.parse(raw);
          if (parsed !== undefined) payload[f.key] = parsed;
        } else {
          payload[f.key] = raw;
        }
      }
      await apiFetch(`/api/tickets/${ticket.id}/custom-fields`, {
        method: 'PATCH',
        json: { customFields: payload },
      });
      await onSaved();
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-[var(--color-t3-purple)]/20 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
          Details
        </h3>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            className="text-xs text-[var(--color-t3-blue)] underline-offset-2 hover:underline"
          >
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-3">
          {fields.map((f) => (
            <div key={f.key}>
              {isLocked(f.key) ? (
                <div>
                  <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                    {f.label}
                  </span>
                  <p className="whitespace-pre-wrap break-words rounded-[var(--radius-button)] border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-body)]">
                    {(() => {
                      const v = ticket.customFields?.[f.key];
                      return v === undefined || v === null || v === '' ? '—' : String(v);
                    })()}
                  </p>
                  {lockedReason && (
                    <span className="mt-1 block text-xs text-[var(--color-muted)]">{lockedReason}</span>
                  )}
                </div>
              ) : f.type === 'textarea' ? (
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                    {f.label}
                  </span>
                  <textarea
                    rows={3}
                    value={draft[f.key] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
                  />
                </label>
              ) : f.type === 'select' ? (
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                    {f.label}
                  </span>
                  <select
                    value={draft[f.key] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
                  >
                    {f.options!.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                    {f.label}
                  </span>
                  <input
                    type={f.type === 'number' ? 'number' : f.type === 'url' ? 'url' : 'text'}
                    name={f.key}
                    value={draft[f.key] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
                  />
                </label>
              )}
            </div>
          ))}
          {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
          <div className="flex gap-2">
            <Button onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="outline" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {fields.map((f) => {
            const v = ticket.customFields?.[f.key];
            return (
              <div key={f.key} className="flex flex-col">
                <dt className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                  {f.label}
                </dt>
                <dd className="whitespace-pre-wrap break-words text-[var(--color-body)]">
                  {v === undefined || v === null || v === '' ? (
                    <span className="text-[var(--color-muted)]">—</span>
                  ) : f.type === 'url' && typeof v === 'string' ? (
                    <a
                      href={v}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-t3-blue)] underline hover:opacity-80"
                    >
                      {v}
                    </a>
                  ) : f.type === 'select' ? (
                    f.options?.find((o) => o.value === String(v))?.label ?? String(v)
                  ) : (
                    String(v)
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}

function initialDraft(ticket: TicketDetail, fields: FieldDef[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    const v = ticket.customFields?.[f.key];
    if (v === null || v === undefined) out[f.key] = '';
    else out[f.key] = String(v);
  }
  return out;
}
